<template>
  <v-card id="acceptance-checklist" class="pa-4 mb-4">
    <div class="d-flex align-center">
      <span class="text-subtitle-1 font-weight-medium mr-2">
        {{ $t("acceptance.checklist.title") }}
      </span>
      <v-chip v-if="unansweredCount" size="small" color="red" variant="tonal">
        {{
          $t("acceptance.checklist.to_answer_count", { count: unansweredCount })
        }}
      </v-chip>
    </div>
    <div class="text-caption text-grey-darken-1 mb-2">
      {{ $t("acceptance.checklist.limit_note", { max }) }}
    </div>

    <div
      v-for="(line, index) in lines"
      :key="line.code"
      class="py-3"
      :class="{ 'acceptance-line-border': index < lines.length - 1 }"
    >
      <div class="d-flex flex-wrap align-center">
        <div class="mr-auto py-1">
          <span class="text-body-2">{{ lineLabel(line) }}</span>
          <v-chip
            v-if="!answerOf(line)"
            size="x-small"
            variant="tonal"
            class="ml-2"
          >
            {{ $t("acceptance.checklist.to_answer") }}
          </v-chip>
          <div v-if="hintOf(line)" class="text-caption text-grey-darken-1">
            {{ hintOf(line) }}
          </div>
        </div>
        <v-btn-toggle
          :model-value="answerOf(line)"
          :disabled="readonly || frozen"
          :class="{
            'acceptance-unsaved': isHighlighted(
              `checklist.${line.code}.answer`,
            ),
          }"
          variant="outlined"
          density="compact"
          color="primary"
          divided
          @update:model-value="setAnswer(line, $event)"
        >
          <v-btn value="present" size="small">
            {{ $t("acceptance.checklist.answers.present") }}
          </v-btn>
          <v-btn value="exception" size="small">
            {{ $t("acceptance.checklist.answers.exception") }}
          </v-btn>
          <v-btn
            value="not_applicable"
            size="small"
            :disabled="!line.allowNotApplicable"
          >
            {{ $t("acceptance.checklist.answers.not_applicable") }}
          </v-btn>
        </v-btn-toggle>
        <!-- Files stay available after confirm; attaching never changes the answer.
             A closable chip hides itself on "×" unless it is controlled, which Vuetify decides
             by model-value AND an update:model-value listener. Both stay, so the chip goes away
             only when the file list no longer has it (after the removal is confirmed). -->
        <v-chip
          v-for="fileId in line.fileIds || []"
          :key="fileId"
          :model-value="true"
          size="small"
          color="primary"
          variant="tonal"
          class="ml-2 my-1"
          :closable="removableIds.includes(fileId) && canAttach"
          :close-label="$t('acceptance.files.remove')"
          @update:model-value="keepVisible"
          @click="$emit('open', fileId)"
          @click:close="$emit('remove', fileId)"
        >
          {{ chipLabel(fileId) }}
        </v-chip>
        <span
          v-if="(line.fileIds || []).length"
          class="text-caption text-grey-darken-1 ml-2"
        >
          {{ line.fileIds.length }}/{{ max }}
        </span>
        <v-btn
          size="small"
          color="primary"
          variant="text"
          class="ml-2"
          :loading="uploadingCode === line.code"
          :disabled="!canAttach || isFull(line)"
          :title="
            isFull(line)
              ? $t('acceptance.files.errors.file_limit_reached', { max })
              : undefined
          "
          @click="pick(line.code)"
        >
          {{ $t("acceptance.checklist.attach") }}
        </v-btn>
      </div>
      <correction-note
        :group="`checklist.${line.code}`"
        :frozen="frozen"
        :corrections="corrections"
        :can-correct="canCorrect"
        :disabled="correctDisabled"
        @correct="$emit('correct', $event)"
        @open-log="$emit('open-log', $event)"
      />

      <v-text-field
        v-if="needsReason(answerOf(line))"
        class="mt-2"
        variant="outlined"
        density="comfortable"
        :label="`${$t('acceptance.checklist.reason')} *`"
        :model-value="values[`checklist.${line.code}.reason`]"
        :readonly="readonly || frozen"
        :bg-color="bg(`checklist.${line.code}.reason`)"
        :rules="[required]"
        maxlength="500"
        @update:model-value="change(`checklist.${line.code}.reason`, $event)"
      />
    </div>

    <input
      ref="picker"
      type="file"
      class="d-none"
      :accept="accept"
      @change="picked"
    />
  </v-card>
</template>

<script>
import moment from "moment";
import {
  FILE_TYPES,
  MAX_FILES_PER_TARGET,
} from "@/components/Acceptance/dossierFiles.mjs";
import CorrectionNote from "@/components/Acceptance/correctionNote.vue";

export default {
  name: "AcceptanceChecklistCard",
  components: { "correction-note": CorrectionNote },
  props: {
    // dossier.checklist: { code, label, allowNotApplicable, naHint, fileIds } per line
    lines: { type: Array, default: () => [] },
    values: { type: Object, required: true },
    readonly: { type: Boolean, default: false },
    highlight: { type: Array, default: () => [] },
    // view.files: metadata (originalName, uploadedAt) by id
    files: { type: Array, default: () => [] },
    // Attaching is separate from answering: it stays on after confirm.
    canAttach: { type: Boolean, default: false },
    // checklist code being uploaded, or null
    uploadingCode: { type: String, default: null },
    // ids the current user may remove (cosmetic; the API decides)
    removableIds: { type: Array, default: () => [] },
    // Confirmed: some fields are frozen and change through a correction (corrections.mjs).
    frozen: { type: Boolean, default: false },
    corrections: { type: Object, default: () => ({}) },
    canCorrect: { type: Boolean, default: false },
    correctDisabled: { type: Boolean, default: false },
  },
  emits: ["change", "attach", "open", "remove", "correct", "open-log"],
  data: () => ({
    pickedCode: null,
  }),
  computed: {
    unansweredCount() {
      return this.lines.filter((line) => !this.answerOf(line)).length;
    },
    accept() {
      return FILE_TYPES.join(",");
    },
    max() {
      return MAX_FILES_PER_TARGET;
    },
    filesById() {
      return Object.fromEntries(this.files.map((file) => [file.id, file]));
    },
  },
  methods: {
    // "Delega_firmata.pdf · aggiunto il 15/10"
    chipLabel(fileId) {
      const file = this.filesById[fileId] || {};
      const name = file.originalName || "—";
      if (!file.uploadedAt) return name;
      const date = moment(file.uploadedAt).format("DD/MM");
      return `${name} · ${this.$t("acceptance.checklist.file_added_on", { date })}`;
    },
    isFull(line) {
      return (line.fileIds || []).length >= MAX_FILES_PER_TARGET;
    },
    // Deliberately empty: its presence makes the chip's visibility ours (see the template).
    keepVisible() {},
    pick(code) {
      this.pickedCode = code;
      this.$refs.picker.value = "";
      this.$refs.picker.click();
    },
    picked(event) {
      const file = event.target.files && event.target.files[0];
      if (file && this.pickedCode) this.$emit("attach", this.pickedCode, file);
      this.pickedCode = null;
    },
    // Lines are configuration copied into the dossier: translate known codes, else show the
    // stored label (Italian source text).
    translated(key, fallback) {
      const text = this.$t(key);
      return text === key ? fallback : text;
    },
    lineLabel(line) {
      return this.translated(
        `acceptance.checklist.lines.${line.code}`,
        line.label,
      );
    },
    hintOf(line) {
      if (!line.naHint) return "";
      return this.translated(
        `acceptance.checklist.na_hints.${line.code}`,
        line.naHint,
      );
    },
    answerOf(line) {
      return this.values[`checklist.${line.code}.answer`] || null;
    },
    needsReason(answer) {
      return answer === "exception" || answer === "not_applicable";
    },
    required(value) {
      return (
        Boolean(value && String(value).trim()) ||
        this.$t("acceptance.errors.reasons.reason_required")
      );
    },
    isHighlighted(path) {
      return this.highlight.includes(path);
    },
    bg(path) {
      return this.isHighlighted(path) ? "amber-lighten-5" : undefined;
    },
    change(path, value) {
      this.$emit("change", path, value);
    },
    setAnswer(line, answer) {
      const value = answer || null;
      this.change(`checklist.${line.code}.answer`, value);
      // The API keeps no reason for "present" or no answer.
      if (!this.needsReason(value)) {
        this.change(`checklist.${line.code}.reason`, null);
      }
    },
  },
};
</script>

<style scoped>
.acceptance-line-border {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.acceptance-unsaved {
  box-shadow: 0 0 0 2px #ffe082;
}
</style>
